import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { faPaperclip } from "@fortawesome/pro-solid-svg-icons/faPaperclip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import FilePreview from "@app/component/FilePreview";
import { downloadFile } from "@app/component/FilePreview/store/actions";
import File from "@app/scene/activity/component/ActivityListPage/File";
import { fetchList } from "@app/scene/activity/store/actions";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";

import FileItem from "./ActivityAnnotation/FilesSection/FileItem";
import styles from "./ActivityFiles.module.scss";

interface Props {
  activity: Activity;
  className?: string;
  doDownload: typeof downloadFile;
  listFetcher: typeof fetchList.request;
}

const ActivityFiles: React.FC<Props> = ({ activity, className, doDownload, listFetcher }) => {
  const [popoverVisible, setPopoverVisibility] = useState<boolean>(false);
  const [editing, startEditing, stopEditing] = useBoolean(!activity.files?.length);
  const [files, setFiles] = useState<RawFile[]>([]);
  const [previewFile, setPreviewFile] = useState<RawFile | undefined>();

  const filesField = activityFieldModel.getByName(ActivityFieldName.FILES);
  const disableEdit = !filesField?.isEditable;

  useEffect(() => {
    (activity.files?.length ? stopEditing : startEditing)();
  }, [activity.files, startEditing, stopEditing]);

  useEffect(() => {
    setFiles(activity.files as RawFile[]);
  }, [activity.files, setFiles]);

  const getUpdatedActivity = useCallback(
    (activity: Activity) => {
      setFiles(activity.files as RawFile[]);
    },
    [setFiles]
  );

  const handleOpenChange = useCallback(
    (visible: boolean) => {
      setPopoverVisibility(visible);
      if (!visible && editing) {
        listFetcher({ request: {} });
      }
      if (!visible && activity.files) {
        stopEditing();
      }
    },
    [activity, editing, listFetcher, setPopoverVisibility, stopEditing]
  );

  const handleDownload = useCallback(
    (file: RawFile) => {
      doDownload?.({ entityId: activity.id, entityType: EntityType.ACTIVITY, file: file });
    },
    [activity.id, doDownload]
  );

  const handleRemoveFile = useCallback(
    (file: RawFile) => {
      if (file) {
        listFetcher({ request: {} });
      }
    },
    [listFetcher]
  );

  if (disableEdit && !activity.files?.length) {
    return null;
  }

  return (
    <>
      <Popover
        content={
          <div className={styles.noteContainer} onClick={stopEvents}>
            {editing ? (
              <File
                activity={activity}
                associatedFiles={files}
                updatedActivity={getUpdatedActivity}
              />
            ) : (
              <>
                {((activity.files ?? []) as RawFile[]).map((file) => (
                  <FileItem
                    file={file}
                    key={file.id}
                    onDownload={handleDownload}
                    onPreview={setPreviewFile}
                  />
                ))}
              </>
            )}
            {!editing && !disableEdit && (
              <Button className={styles.addEditButton} onClick={startEditing} type="link">
                {
                  <FormattedMessage
                    id="activity.activityFiles.editFiles"
                    defaultMessage="Edit or Add Files"
                    description="message for edit or add files button"
                  />
                }
              </Button>
            )}
          </div>
        }
        destroyTooltipOnHide
        onOpenChange={handleOpenChange}
        open={popoverVisible}
        placement="bottomLeft"
        trigger={!activity.files || editing ? ["click"] : ["hover"]}
      >
        <div
          className={cn(styles.container, activity.files?.length && styles.hasFiles, className)}
          onClick={stopEvents}
        >
          <FontAwesomeIcon className={styles.icon} icon={faPaperclip} />
          <span className={styles.label}>
            {activity.files && activity.files.length > 0 ? (
              <FontAwesomeIcon className={styles.icon} icon={faAngleDown} />
            ) : (
              <FormattedMessage
                id="activity.activityFiles.addFile"
                defaultMessage="Add File"
                description="message for add files button"
              />
            )}
          </span>
        </div>
      </Popover>
      {previewFile && (
        <FilePreview
          disabled={disableEdit}
          entityId={activity.id}
          entityType={EntityType.ACTIVITY}
          file={previewFile}
          onHide={() => setPreviewFile(undefined)}
          onRemove={handleRemoveFile}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  doDownload: downloadFile,
  listFetcher: fetchList.request,
};

export default connect(null, mapDispatchToProps)(ActivityFiles);
